import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_MfaStore } from './MfaStore';
import { TOKEN_AuthStore } from '@/views/AuthStore';
import { CopyButton } from '@/views/CopyButton';

/** The account "Two-factor authentication" section (U1): factor list, TOTP enrollment with a
 * client-drawn QR, security-key add/remove, recovery-code regenerate. Enroll/remove revoke every
 * session (the server's watermark rule). The section swaps to a sign-in-again banner after. */
export const MfaView = observer(function MfaView() {
  const mfa = useResolve(TOKEN_MfaStore);
  const auth = useResolve(TOKEN_AuthStore);
  // Local, ephemeral form state (the mvvm-view carve-out).
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [keyName, setKeyName] = useState('');

  useEffect(() => {
    void mfa.load();
  }, [mfa]);

  const pwField = (
    <div>
      <label className="block text-sm text-slate-400" htmlFor="mfa-pw">
        Current password (required for any security change)
      </label>
      <input
        id="mfa-pw"
        type="password"
        autoComplete="current-password"
        className="w-full max-w-sm rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        data-testid="mfa-password"
      />
    </div>
  );

  return (
    <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800" data-testid="mfa-section">
      <h3 className="mb-2 text-lg font-semibold">Two-factor authentication</h3>

      {mfa.error && (
        <div className="mb-2 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:bg-red-950/30" data-testid="mfa-error">
          {mfa.error}
        </div>
      )}
      {mfa.notice && !mfa.reauthRequired && (
        <div className="mb-2 rounded border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-800 dark:bg-emerald-950/30" data-testid="mfa-notice">
          {mfa.notice}
        </div>
      )}

      {/* The one-time recovery codes: rendered ABOVE the re-login banner so a first-factor
          enrollment shows the codes before the user signs out. */}
      {mfa.recoveryCodes && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30" data-testid="recovery-codes">
          <p className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-200">
            Recovery codes: shown ONCE. Store them somewhere safe; each works one time.
          </p>
          <pre className="mb-2 font-mono text-sm" data-testid="recovery-codes-list">
            {mfa.recoveryCodes.join('\n')}
          </pre>
          <div className="flex gap-2">
            <CopyButton text={mfa.recoveryCodes.join('\n')} label="Copy all" />
            <button type="button" className="text-sm underline" onClick={() => mfa.dismissCodes()} data-testid="recovery-codes-dismiss">
              I’ve saved them
            </button>
          </div>
        </div>
      )}

      {mfa.reauthRequired ? (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30" data-testid="mfa-reauth-required">
          Security settings changed. Every session was signed out.{' '}
          <button type="button" onClick={() => void auth.logout()} className="underline">
            Sign in again
          </button>
          .
        </div>
      ) : mfa.loading || !mfa.status ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="space-y-5">
          {pwField}

          {/* TOTP */}
          <div data-testid="totp-block">
            <h4 className="font-medium">Authenticator app (TOTP)</h4>
            {mfa.status.totp_enabled ? (
              <div className="mt-1 flex items-center gap-3 text-sm">
                <span className="text-emerald-600" data-testid="totp-enrolled">
                  ✓ enrolled
                </span>
                <button
                  type="button"
                  className="rounded border border-red-300 px-2 py-0.5 text-red-700 disabled:opacity-50"
                  disabled={mfa.busy}
                  onClick={() => void mfa.totpRemove(pw)}
                  data-testid="totp-remove"
                >
                  Remove
                </button>
              </div>
            ) : mfa.pendingTotp ? (
              <div className="mt-2 space-y-2 text-sm" data-testid="totp-pending">
                <p>Scan the QR code (or enter the secret) in your authenticator app, then confirm with a live code.</p>
                <img src={mfa.pendingTotp.qrDataUrl} alt="TOTP enrollment QR code" className="h-40 w-40 bg-white p-1" data-testid="totp-qr" />
                <p className="font-mono text-xs" data-testid="totp-secret">
                  {mfa.pendingTotp.secret} <CopyButton text={mfa.pendingTotp.secret} label="Copy" />
                </p>
                <div className="flex items-center gap-2">
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="6-digit code"
                    className="w-28 rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="totp-code"
                  />
                  <button
                    type="button"
                    className="rounded bg-slate-800 px-3 py-1 text-white disabled:opacity-50 dark:bg-slate-200 dark:text-slate-900"
                    disabled={mfa.busy}
                    onClick={() => void mfa.totpConfirm(code)}
                    data-testid="totp-confirm"
                  >
                    Confirm
                  </button>
                  <button type="button" className="text-sm underline" onClick={() => mfa.cancelTotp()} data-testid="totp-cancel">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="mt-1 rounded border border-slate-300 px-2 py-0.5 text-sm disabled:opacity-50 dark:border-slate-700"
                disabled={mfa.busy}
                onClick={() => void mfa.totpStart(pw)}
                data-testid="totp-start"
              >
                Set up authenticator app
              </button>
            )}
          </div>

          {/* Security keys / passkeys */}
          <div data-testid="webauthn-block">
            <h4 className="font-medium">Security keys & passkeys</h4>
            {mfa.status.webauthn.length === 0 && <p className="mt-1 text-sm text-slate-400">None enrolled.</p>}
            <ul className="mt-1 space-y-1">
              {mfa.status.webauthn.map((c) => (
                <li key={c.cred_id} className="flex items-center gap-3 text-sm" data-testid={`webauthn-cred-${c.cred_id}`}>
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-slate-400">added {new Date(c.added_ts * 1000).toLocaleDateString()}</span>
                  <button
                    type="button"
                    className="rounded border border-red-300 px-2 py-0.5 text-red-700 disabled:opacity-50"
                    disabled={mfa.busy}
                    onClick={() => void mfa.webauthnRemove(c.cred_id, pw)}
                    data-testid={`webauthn-remove-${c.cred_id}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {mfa.webauthnSupported ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  placeholder="Key name (e.g. YubiKey 5)"
                  maxLength={64}
                  className="w-56 rounded border border-slate-300 bg-transparent px-2 py-1 text-sm dark:border-slate-700"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  data-testid="webauthn-name"
                />
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-0.5 text-sm disabled:opacity-50 dark:border-slate-700"
                  disabled={mfa.busy}
                  onClick={() => void mfa.webauthnAdd(pw, keyName)}
                  data-testid="webauthn-add"
                >
                  Add a security key
                </button>
              </div>
            ) : (
              <p className="mt-1 text-sm text-slate-400">This browser cannot enroll a security key.</p>
            )}
            <p className="mt-1 text-xs text-slate-400">
              Tip: enroll a second key on another device as your preferred backup; recovery codes are the last resort.
            </p>
          </div>

          {/* Recovery codes */}
          <div data-testid="recovery-block">
            <h4 className="font-medium">Recovery codes</h4>
            <p className="mt-1 text-sm" data-testid="recovery-remaining">
              {mfa.status.recovery_codes_remaining} unused code{mfa.status.recovery_codes_remaining === 1 ? '' : 's'} remaining.
            </p>
            <button
              type="button"
              className="mt-1 rounded border border-slate-300 px-2 py-0.5 text-sm disabled:opacity-50 dark:border-slate-700"
              disabled={mfa.busy}
              onClick={() => void mfa.regenerateCodes(pw)}
              data-testid="recovery-regenerate"
            >
              Generate new codes
            </button>
          </div>
        </div>
      )}
    </section>
  );
});
